// Phase 3A.2 — Partner / Franchisee Guided Compliance Dashboard
// =============================================================
// This is the partner-facing surface. It deliberately surfaces ONLY:
//   • product status                (APPROVED / PENDING / ACTION REQUIRED / …)
//   • correction cards              (high-level rule guidance)
//   • missing documents             (with upload CTA)
//   • state availability warnings   (plain-English, no legal analysis)
//   • next steps                    (clear actions)
//   • approval status               (their profile review state)
//
// It NEVER surfaces internal legal notes, the state matrix, audit logs,
// attorney review notes, the compliance intelligence engine, the source
// library, or the compliance kill switch.

import React, { useEffect, useState } from "react";
import {
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Clock,
  Upload,
  Info,
  MapPin,
  Loader2,
  BookOpen,
} from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_TONE = {
  "APPROVED":                   { tone: "bg-lifted-green/15 text-lifted-green border-lifted-green/30", icon: ShieldCheck },
  "PENDING REVIEW":             { tone: "bg-amber-400/15 text-amber-200 border-amber-400/30", icon: Clock },
  "ACTION REQUIRED":            { tone: "bg-rose-500/15 text-rose-200 border-rose-500/30", icon: AlertTriangle },
  "DOCUMENT REQUIRED":          { tone: "bg-sky-500/15 text-sky-200 border-sky-500/30", icon: FileText },
  "LIMITED AVAILABILITY":       { tone: "bg-violet-500/15 text-violet-200 border-violet-500/30", icon: MapPin },
  "STATE RESTRICTED":           { tone: "bg-violet-500/15 text-violet-200 border-violet-500/30", icon: MapPin },
  "COMPLIANCE REVIEW REQUIRED": { tone: "bg-orange-500/15 text-orange-200 border-orange-500/30", icon: ShieldAlert },
  "BLOCKED":                    { tone: "bg-rose-700/20 text-rose-100 border-rose-600/40", icon: ShieldAlert },
};

const APPROVAL_TONE = {
  APPROVED:  "bg-lifted-green/15 text-lifted-green border-lifted-green/30",
  PENDING:   "bg-amber-400/15 text-amber-200 border-amber-400/30",
  REJECTED:  "bg-rose-500/15 text-rose-200 border-rose-500/30",
  SUSPENDED: "bg-rose-700/20 text-rose-100 border-rose-600/40",
};

function withTimeout(promise, ms = 8000) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("Request timed out")), ms)),
  ]);
}

async function safeFetchJson(path, timeoutMs = 8000) {
  const r = await withTimeout(fetch(path, { credentials: "include" }), timeoutMs);
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    const err = new Error(body.detail || `Request failed (${r.status})`);
    err.status = r.status;
    throw err;
  }
  return r.json();
}

export default function PartnerCompliancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = await safeFetchJson(`/api/partner-compliance/me/dashboard`);
        if (!cancelled) setData(d);
      } catch (e) {
        console.error("partner-compliance dashboard failed", e);
        if (!cancelled) {
          setError(
            e.status === 403
              ? "This dashboard is for partners and franchisees only."
              : "We couldn't load your compliance dashboard. Please try again."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="p-8 text-lifted-dim flex items-center gap-2" data-testid="partner-compliance-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your compliance dashboard…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8" data-testid="partner-compliance-error">
        <div className="glass-panel p-5 border border-amber-500/40">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-300 mt-[2px]" />
            <p className="text-sm text-amber-100/80">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const counts = data.counts || {};
  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-6xl" data-testid="partner-compliance-page">
      {/* ----- Header + Approval Status ----- */}
      <header className="space-y-2">
        <div className="text-xs uppercase tracking-[0.2em] text-lifted-green">Compliance Center</div>
        <h1 className="font-anton uppercase text-4xl text-white">Your Compliance Dashboard</h1>
        <p className="text-lifted-dim text-sm max-w-2xl">
          A guided view of your storefront&apos;s compliance status. Action items and
          required documents are listed below. Lifted compliance reviews every
          product and updates this dashboard automatically — there is nothing for
          you to do on the internal compliance engine itself.
        </p>
        <div
          className={`inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full border text-xs uppercase tracking-wider font-bebas ${
            APPROVAL_TONE[data.approval_status] || APPROVAL_TONE.PENDING
          }`}
          data-testid="partner-compliance-approval-status"
        >
          <ShieldCheck size={14} /> Approval: {data.approval_status}
        </div>
        <p className="text-xs text-lifted-dim mt-1 max-w-2xl">
          {data.approval_message}
        </p>
      </header>

      {/* ----- Status counts ----- */}
      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="partner-compliance-counts">
        {[
          "APPROVED", "PENDING REVIEW", "ACTION REQUIRED", "DOCUMENT REQUIRED",
          "LIMITED AVAILABILITY", "STATE RESTRICTED", "COMPLIANCE REVIEW REQUIRED", "BLOCKED",
        ].map((label) => {
          const Tone = STATUS_TONE[label];
          const Icon = Tone?.icon || Info;
          return (
            <div
              key={label}
              className={`glass-panel p-3 rounded-xl border ${Tone?.tone || "border-white/10"}`}
              data-testid={`partner-compliance-count-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center gap-2">
                <Icon size={14} />
                <span className="text-[10px] uppercase tracking-wider font-bebas">{label}</span>
              </div>
              <div className="font-anton text-3xl mt-1">{counts[label] || 0}</div>
            </div>
          );
        })}
      </section>

      {/* ----- Next steps ----- */}
      <section className="glass-panel p-5" data-testid="partner-compliance-next-steps">
        <div className="flex items-center gap-2 mb-3">
          <Info size={16} className="text-lifted-green" />
          <h2 className="font-bebas tracking-wider text-white text-lg uppercase">Next steps</h2>
        </div>
        <ul className="space-y-2">
          {(data.next_steps || []).map((s) => (
            <li
              key={s.key}
              className="flex items-start justify-between gap-4 text-sm text-white/90"
              data-testid={`partner-compliance-next-${s.key}`}
            >
              <span>{s.label}</span>
              {s.cta && s.url && (
                <Link to={s.url} className="text-lifted-green underline shrink-0">
                  {s.cta} →
                </Link>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* ----- Documents Required ----- */}
      {data.documents_required?.length > 0 && (
        <section className="glass-panel p-5" data-testid="partner-compliance-documents-required">
          <div className="flex items-center gap-2 mb-3">
            <Upload size={16} className="text-sky-300" />
            <h2 className="font-bebas tracking-wider text-white text-lg uppercase">
              Documents required
            </h2>
          </div>
          <ul className="space-y-2">
            {data.documents_required.map((d) => (
              <li
                key={d.id}
                className="flex items-start justify-between gap-4 text-sm border-l-2 border-sky-300/40 pl-3"
                data-testid={`partner-compliance-doc-${d.id}`}
              >
                <div>
                  <div className="font-bebas tracking-wider text-sky-200 uppercase">{d.label}</div>
                  <div className="text-xs text-lifted-dim mt-0.5">{d.message}</div>
                  {d.due_at && (
                    <div className="text-[10px] text-amber-200 mt-0.5">
                      Due {new Date(d.due_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <Link
                  to="/partner/documents"
                  className="text-lifted-green underline shrink-0"
                  data-testid={`partner-compliance-doc-upload-${d.id}`}
                >
                  Upload →
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ----- Documents / Licenses Expiring ----- */}
      {data.documents_expiring?.length > 0 && (
        <section className="glass-panel p-5" data-testid="partner-compliance-documents-expiring">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={16} className="text-amber-300" />
            <h2 className="font-bebas tracking-wider text-white text-lg uppercase">
              Documents expiring
            </h2>
          </div>
          <ul className="space-y-2 text-sm">
            {data.documents_expiring.map((d) => (
              <li key={d.kind} className="flex items-center justify-between">
                <span>
                  <span className="font-bebas tracking-wider text-amber-200 uppercase">
                    {d.label}
                  </span>
                  <span className="text-xs text-lifted-dim ml-2">
                    expires {new Date(d.expires_at).toLocaleDateString()}
                  </span>
                </span>
                <span className="text-xs text-amber-100/80">{d.days_left} days left</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ----- Training required ----- */}
      {data.training_required?.length > 0 && (
        <section className="glass-panel p-5" data-testid="partner-compliance-training">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={16} className="text-lifted-green" />
            <h2 className="font-bebas tracking-wider text-white text-lg uppercase">
              Training required
            </h2>
          </div>
          <ul className="space-y-2 text-sm">
            {data.training_required.map((t) => (
              <li key={t.key} className="flex items-center justify-between">
                <span>{t.label}</span>
                <Link to={t.url} className="text-lifted-green underline">
                  Open training →
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ----- Products by status (only buckets with content) ----- */}
      {[
        "action_required", "document_required", "state_restricted",
        "limited_availability", "compliance_review_required", "blocked",
        "pending_review", "approved",
      ].map((bucketKey) => {
        const items = data.products?.[bucketKey] || [];
        if (!items.length) return null;
        const heading = bucketKey
          .replace(/_/g, " ")
          .toUpperCase();
        const Tone = STATUS_TONE[heading];
        return (
          <section
            key={bucketKey}
            className="glass-panel p-5"
            data-testid={`partner-compliance-bucket-${bucketKey}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={16} className="text-lifted-green" />
              <h2 className="font-bebas tracking-wider text-white text-lg uppercase">
                {heading} — {items.length}
              </h2>
            </div>
            <ul className="space-y-3">
              {items.map((p) => (
                <li
                  key={p.product_handle}
                  className={`border rounded-xl p-3 ${Tone?.tone || "border-white/10"}`}
                  data-testid={`partner-compliance-product-${p.product_handle}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-bebas tracking-wider text-base uppercase">
                        {p.title || p.product_handle}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider opacity-70">
                        {p.status}
                      </div>
                    </div>
                  </div>
                  {/* Correction cards */}
                  {p.correction_cards?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {p.correction_cards.map((c, idx) => (
                        <div
                          key={idx}
                          className="bg-black/30 rounded-lg p-3 text-sm space-y-1"
                          data-testid={`correction-card-${p.product_handle}-${idx}`}
                        >
                          <div className="font-bebas tracking-wider text-white uppercase">
                            {c.issue}
                          </div>
                          <div className="text-xs text-white/80">
                            <span className="text-lifted-green uppercase tracking-wider mr-1">
                              Why it matters:
                            </span>
                            {c.why_it_matters}
                          </div>
                          <div className="text-xs text-white/80">
                            <span className="text-lifted-green uppercase tracking-wider mr-1">
                              Suggested:
                            </span>
                            {c.suggested_revision}
                          </div>
                          <div className="text-xs text-white/60 flex flex-wrap gap-x-3">
                            <span>
                              <strong className="text-white">Next step:</strong> {c.next_step}
                            </span>
                            <span>
                              <strong className="text-white">Est. review:</strong>{" "}
                              {c.estimated_review_minutes} min
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* State warnings */}
                  {p.state_warnings?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {p.state_warnings.map((w, idx) => (
                        <div
                          key={idx}
                          className="border border-violet-400/30 bg-violet-500/10 rounded-lg p-3 text-xs"
                          data-testid={`state-warning-${p.product_handle}-${idx}`}
                        >
                          <div className="text-violet-200">{w.message}</div>
                          {w.affected_states?.length > 0 && (
                            <div className="text-[10px] text-violet-100/70 mt-1">
                              Affected: {w.affected_states.join(", ")}
                            </div>
                          )}
                          <div className="text-[10px] text-violet-100/60 mt-1">
                            {w.review_status}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {p.next_step && !p.correction_cards?.length && (
                    <div className="mt-2 text-xs text-lifted-dim">{p.next_step}</div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      {/* ----- Compliance messages ----- */}
      {data.messages?.length > 0 && (
        <section className="glass-panel p-5" data-testid="partner-compliance-messages">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 size={16} className="text-lifted-green" />
            <h2 className="font-bebas tracking-wider text-white text-lg uppercase">
              Compliance messages
            </h2>
          </div>
          <ul className="space-y-2 text-sm">
            {data.messages.map((m) => (
              <li
                key={m.id}
                className="border-l-2 border-lifted-green/40 pl-3"
                data-testid={`partner-compliance-message-${m.id}`}
              >
                <div className="font-bebas tracking-wider text-white uppercase">{m.subject}</div>
                <div className="text-xs text-white/80 mt-1">{m.body}</div>
                <div className="text-[10px] text-lifted-dim mt-1">
                  {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
